import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { challengesAPI, paymentsAPI, couponsAPI, platformAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Check, X, Loader2, RefreshCw, TrendingUp, Shield, Target, Zap, 
  Tag, Sparkles, Trophy, Coins, ArrowRight, Info, Clock
} from 'lucide-react';

const GetFunded = () => {
  const [challengePackages, setChallengePackages] = useState([]);
  const [instantPackages, setInstantPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [upgradeProfitSplit, setUpgradeProfitSplit] = useState({});
  const [payoutOnDemand, setPayoutOnDemand] = useState({});
  const [platformSettings, setPlatformSettings] = useState(null);
  const [activeTab, setActiveTab] = useState('evaluation');
  const { user } = useAuth();
  const navigate = useNavigate();
  const tabsSectionRef = React.useRef(null);

  const scrollToPackages = (tab) => {
    setActiveTab(tab);
    setTimeout(() => {
      tabsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  // KBTCOIN rewards for Instant Funded accounts
  const kbtcoinRewards = {
    'instant_50k': 10,
    'instant_100k': 20,
    'instant_200k': 40
  };

  // Elite Funded Trader accounts
  const eliteAccounts = [
    { id: 'elite_50k', size: 50000, price: 4999, dailyDrawdown: 10, maxDrawdown: 20, profitSplit: 80 },
    { id: 'elite_100k', size: 100000, price: 9499, dailyDrawdown: 10, maxDrawdown: 20, profitSplit: 80 },
    { id: 'elite_200k', size: 200000, price: 17999, dailyDrawdown: 10, maxDrawdown: 20, profitSplit: 80 },
  ];

  useEffect(() => {
    fetchPackages();
    fetchPlatformSettings();
  }, []);

  const fetchPlatformSettings = async () => {
    try {
      const response = await platformAPI.getSettings();
      setPlatformSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch platform settings');
    }
  };

  const fetchPackages = async () => {
    try {
      const [challengeRes, instantRes] = await Promise.all([
        challengesAPI.getPackages(),
        challengesAPI.getInstantPackages()
      ]);
      setChallengePackages(challengeRes.data);
      setInstantPackages(instantRes.data);
    } catch (error) {
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const handleValidateCoupon = async (packageId) => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    
    setValidatingCoupon(true);
    try {
      const response = await couponsAPI.validate(couponCode.toUpperCase(), packageId);
      if (response.data.valid) {
        setAppliedCoupon(response.data);
        setSelectedPackage(packageId);
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
        setAppliedCoupon(null);
      }
    } catch (error) {
      toast.error('Failed to validate coupon');
      setAppliedCoupon(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const clearCoupon = () => {
    setCouponCode('');
    setAppliedCoupon(null);
    setSelectedPackage(null);
  };

  const handlePurchase = async (packageId) => {
    if (!user) {
      toast.info('Please create an account or log in to continue');
      navigate('/register', { state: { returnTo: '/get-funded', packageId } });
      return;
    }

    setPurchasing(packageId);
    try {
      const originUrl = window.location.origin;
      const coupon = selectedPackage === packageId ? couponCode : null;
      const upgrade = upgradeProfitSplit[packageId] || false;
      const onDemand = payoutOnDemand[packageId] || false;
      const response = await paymentsAPI.createCheckout(packageId, originUrl, coupon, upgrade, onDemand);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setPurchasing(null);
    }
  };

  const getDisplayPrice = (pkg) => {
    let basePrice = pkg.price;
    
    if (appliedCoupon && selectedPackage === pkg.package_id) {
      basePrice = appliedCoupon.final_price;
    }
    
    if (upgradeProfitSplit[pkg.package_id]) {
      basePrice += pkg.price * 0.25;
    }
    
    if (payoutOnDemand[pkg.package_id]) {
      basePrice += pkg.price * 0.15;
    }
    
    return basePrice;
  };

  const getUpgradeFee = (price) => price * 0.25;
  const getPayoutOnDemandFee = (price) => price * 0.15;

  const toggleUpgrade = (packageId) => {
    setUpgradeProfitSplit(prev => ({ ...prev, [packageId]: !prev[packageId] }));
  };

  const togglePayoutOnDemand = (packageId) => {
    setPayoutOnDemand(prev => ({ ...prev, [packageId]: !prev[packageId] }));
  };

  const tradingRules = platformSettings?.trading_rules || {
    profit_target_phase1: 8,
    profit_target_phase2: 5,
    max_daily_loss: 5,
    max_total_loss: 10
  };

  // Comparison features for the table
  const comparisonFeatures = [
    { name: 'Account Type', evaluation: 'Two-Phase Challenge', instant: 'Instant Funded', elite: 'Elite Funded Trader' },
    { name: 'Evaluation Required', evaluation: 'Yes (Phase 1 & 2)', instant: 'No', elite: 'Selection-Based' },
    { name: 'Time Limit', evaluation: 'Unlimited', instant: 'Unlimited', elite: 'Unlimited' },
    { name: 'Phase 1 Target', evaluation: `${tradingRules.profit_target_phase1}%`, instant: 'N/A', elite: 'N/A' },
    { name: 'Phase 2 Target', evaluation: `${tradingRules.profit_target_phase2}%`, instant: 'N/A', elite: 'N/A' },
    { name: 'Daily Drawdown', evaluation: `${tradingRules.max_daily_loss}%`, instant: `${tradingRules.max_daily_loss}%`, elite: '10%' },
    { name: 'Max Drawdown', evaluation: `${tradingRules.max_total_loss}%`, instant: `${tradingRules.max_total_loss}%`, elite: '20%' },
    { name: '2% Floating Loss Rule', evaluation: true, instant: true, elite: false },
    { name: 'Min. Trading Days (Payout)', evaluation: '6 days', instant: '6 days', elite: '6 days' },
    { name: 'Profit Split', evaluation: 'Up to 100%', instant: '90% (100% +fee)', elite: '80%' },
    { name: 'Free Retake', evaluation: true, instant: false, elite: false },
    { name: 'KBTCOIN Rewards', evaluation: false, instant: true, elite: 'TBC' },
    { name: 'Real Capital', evaluation: false, instant: false, elite: true },
    { name: 'Guaranteed Payouts', evaluation: '100%', instant: '100%', elite: '100%' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16 bg-[hsl(220,20%,4%)]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(220,20%,4%)] pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <Badge className="mb-4 px-4 py-1 bg-blue-500/10 text-blue-400 border-blue-500/20">
            COMPARE & CHOOSE
          </Badge>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight mb-4 text-white" data-testid="getfunded-title">
            GET <span className="gradient-text">FUNDED</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto text-sm sm:text-base">
            Compare all funding options. Choose your path. Start trading.
          </p>
        </motion.div>

        {/* COMPREHENSIVE COMPARISON TABLE */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8 sm:mb-12"
        >
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-white text-center mb-6">
            Compare All Programs
          </h2>
          
          {/* Mobile Comparison Cards */}
          <div className="block lg:hidden space-y-4">
            {/* Evaluation Card */}
            <Card className="bg-[hsl(220,20%,7%)] border-blue-500/30" data-testid="compare-evaluation-mobile">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-blue-400 flex items-center gap-2 text-lg">
                    <Target className="w-5 h-5" />
                    Evaluation
                  </CardTitle>
                  <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">2 Phases</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><p className="text-gray-500">Phase 1</p><p className="text-white font-medium">{tradingRules.profit_target_phase1}%</p></div>
                  <div><p className="text-gray-500">Phase 2</p><p className="text-white font-medium">{tradingRules.profit_target_phase2}%</p></div>
                  <div><p className="text-gray-500">Daily DD</p><p className="text-white font-medium">{tradingRules.max_daily_loss}%</p></div>
                  <div><p className="text-gray-500">Max DD</p><p className="text-white font-medium">{tradingRules.max_total_loss}%</p></div>
                </div>
                <div className="flex items-center gap-2 text-green-400 text-sm"><Check className="w-4 h-4" /><span>Free Retake for first-timers</span></div>
                <div className="flex items-center gap-2 text-green-400 text-sm"><Check className="w-4 h-4" /><span>Up to 100% Profit Split</span></div>
                <Button className="w-full mt-2 bg-blue-500 hover:bg-blue-600" onClick={() => scrollToPackages('evaluation')} data-testid="compare-view-evaluation">View Packages</Button>
              </CardContent>
            </Card>

            {/* Instant Funded Card */}
            <Card className="bg-[hsl(220,20%,7%)] border-cyan-500/30" data-testid="compare-instant-mobile">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-cyan-400 flex items-center gap-2 text-lg">
                    <Zap className="w-5 h-5" />
                    Instant Funded
                  </CardTitle>
                  <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><Coins className="w-3 h-3 mr-1" />+KBTCOIN</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><p className="text-gray-500">Evaluation</p><p className="text-cyan-400 font-medium">None</p></div>
                  <div><p className="text-gray-500">Profit Split</p><p className="text-white font-medium">90%</p></div>
                  <div><p className="text-gray-500">Daily DD</p><p className="text-white font-medium">{tradingRules.max_daily_loss}%</p></div>
                  <div><p className="text-gray-500">Max DD</p><p className="text-white font-medium">{tradingRules.max_total_loss}%</p></div>
                </div>
                <div className="flex items-center gap-2 text-green-400 text-sm"><Sparkles className="w-4 h-4" /><span>100% Split available (+25% fee)</span></div>
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
                  <p className="text-yellow-400 font-medium text-sm mb-2 flex items-center gap-1"><Coins className="w-4 h-4" />KBTCOIN Rewards:</p>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="text-center"><p className="text-gray-400">$50K</p><p className="text-yellow-400 font-bold">10</p></div>
                    <div className="text-center"><p className="text-gray-400">$100K</p><p className="text-yellow-400 font-bold">20</p></div>
                    <div className="text-center"><p className="text-gray-400">$200K</p><p className="text-yellow-400 font-bold">40</p></div>
                  </div>
                </div>
                <Button className="w-full mt-2 bg-cyan-500 hover:bg-cyan-600" onClick={() => scrollToPackages('instant')} data-testid="compare-view-instant">Get Instant Funding</Button>
              </CardContent>
            </Card>

            {/* Elite Funded Card */}
            <Card className="bg-[hsl(220,20%,7%)] border-green-500/30" data-testid="compare-elite-mobile">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-green-400 flex items-center gap-2 text-lg">
                    <Trophy className="w-5 h-5" />
                    Elite Funded
                  </CardTitle>
                  <div className="flex gap-1">
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Real Capital</Badge>
                    <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs">KBTCOIN TBC</Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><p className="text-gray-500">Access</p><p className="text-green-400 font-medium">Selection</p></div>
                  <div><p className="text-gray-500">Split</p><p className="text-white font-medium">80%</p></div>
                  <div><p className="text-gray-500">Daily DD</p><p className="text-white font-medium">10%</p></div>
                  <div><p className="text-gray-500">Max DD</p><p className="text-white font-medium">20%</p></div>
                </div>
                <div className="flex items-center gap-2 text-green-400 text-sm"><Check className="w-4 h-4" /><span>100% Guaranteed Payouts</span></div>
                <div className="flex items-center gap-2 text-green-400 text-sm"><Check className="w-4 h-4" /><span>No 2% Floating Loss Rule</span></div>
                <Button className="w-full mt-2 bg-green-500 hover:bg-green-600" onClick={() => scrollToPackages('elite')} data-testid="compare-view-elite">Learn More</Button>
              </CardContent>
            </Card>
          </div>

          {/* Desktop Comparison Table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full border-collapse" data-testid="comparison-table">
              <thead>
                <tr>
                  <th className="p-4 text-left text-gray-400 font-medium border-b border-white/10 bg-[hsl(220,20%,6%)]">Feature</th>
                  <th className="p-4 text-center border-b border-blue-500/30 bg-blue-500/5 cursor-pointer hover:bg-blue-500/10 transition-colors" onClick={() => scrollToPackages('evaluation')}>
                    <div className="flex flex-col items-center gap-2">
                      <Target className="w-6 h-6 text-blue-400" />
                      <span className="text-blue-400 font-bold">Evaluation</span>
                    </div>
                  </th>
                  <th className="p-4 text-center border-b border-cyan-500/30 bg-cyan-500/5 cursor-pointer hover:bg-cyan-500/10 transition-colors" onClick={() => scrollToPackages('instant')}>
                    <div className="flex flex-col items-center gap-2">
                      <Zap className="w-6 h-6 text-cyan-400" />
                      <span className="text-cyan-400 font-bold">Instant Funded</span>
                      <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs"><Coins className="w-3 h-3 mr-1" />+KBTCOIN</Badge>
                    </div>
                  </th>
                  <th className="p-4 text-center border-b border-green-500/30 bg-green-500/5 cursor-pointer hover:bg-green-500/10 transition-colors" onClick={() => scrollToPackages('elite')}>
                    <div className="flex flex-col items-center gap-2">
                      <Trophy className="w-6 h-6 text-green-400" />
                      <span className="text-green-400 font-bold">Elite Funded</span>
                      <div className="flex gap-1">
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">Real Capital</Badge>
                        <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs">KBTCOIN TBC</Badge>
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((feature, index) => (
                  <tr key={index} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="p-4 text-gray-300 font-medium">{feature.name}</td>
                    <td className="p-4 text-center">
                      {typeof feature.evaluation === 'boolean' ? (
                        feature.evaluation ? <Check className="w-5 h-5 text-green-400 mx-auto" /> : <X className="w-5 h-5 text-gray-600 mx-auto" />
                      ) : <span className="text-white">{feature.evaluation}</span>}
                    </td>
                    <td className="p-4 text-center">
                      {typeof feature.instant === 'boolean' ? (
                        feature.instant ? <Check className="w-5 h-5 text-green-400 mx-auto" /> : <X className="w-5 h-5 text-gray-600 mx-auto" />
                      ) : <span className="text-white">{feature.instant}</span>}
                    </td>
                    <td className="p-4 text-center">
                      {typeof feature.elite === 'boolean' ? (
                        feature.elite ? <Check className="w-5 h-5 text-green-400 mx-auto" /> : <X className="w-5 h-5 text-gray-600 mx-auto" />
                      ) : <span className="text-white">{feature.elite}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Coupon Code Input */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-6">
          <Card className="bg-[hsl(220,20%,7%)] border border-white/10 shadow-lg max-w-md mx-auto">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4 text-blue-400 shrink-0" />
                <span className="text-sm font-medium text-gray-300">Have a coupon?</span>
              </div>
              <div className="flex gap-2 mt-2">
                <Input
                  placeholder="Enter coupon code"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-blue-500 uppercase rounded-lg"
                  data-testid="coupon-input"
                />
                {appliedCoupon && (
                  <Button variant="outline" onClick={clearCoupon} className="border-white/10 text-white hover:bg-white/5" data-testid="clear-coupon">
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {appliedCoupon && (
                <div className="mt-2 text-sm text-green-400 flex items-center gap-1">
                  <Check className="w-4 h-4" />
                  {appliedCoupon.discount_percent}% off - Save ${appliedCoupon.discount_amount}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* FREE RETAKE Banner */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-8">
          <Card className="bg-gradient-to-r from-blue-600/20 via-cyan-600/20 to-blue-600/20 border border-blue-500/30 shadow-xl overflow-hidden relative">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 rounded-full blur-3xl" />
            </div>
            <CardContent className="p-4 sm:p-6 md:p-8 relative z-10">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-blue-500/20 backdrop-blur border border-blue-500/30 flex items-center justify-center">
                    <RefreshCw className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400" />
                  </div>
                  <div className="text-center md:text-left">
                    <h3 className="font-heading text-xl sm:text-2xl md:text-3xl font-black text-white">FREE RETAKE INCLUDED!</h3>
                    <p className="text-gray-300 text-xs sm:text-sm md:text-base">First-time buyers get a <span className="font-bold text-cyan-400">complimentary retry</span> if they don't pass</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-green-500/20 backdrop-blur rounded-full px-4 py-2 border border-green-500/30">
                  <Check className="w-5 h-5 text-green-400" />
                  <span className="text-white font-bold text-sm">No Extra Cost</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* PRICING TABS */}
        <div ref={tabsSectionRef}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-lg mx-auto grid-cols-3 mb-8 bg-[hsl(220,20%,7%)] border border-white/10">
            <TabsTrigger value="evaluation" className="font-heading uppercase text-xs sm:text-sm data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400" data-testid="tab-evaluation">
              Evaluation
            </TabsTrigger>
            <TabsTrigger value="instant" className="font-heading uppercase text-xs sm:text-sm data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400" data-testid="tab-instant">
              Instant
            </TabsTrigger>
            <TabsTrigger value="elite" className="font-heading uppercase text-xs sm:text-sm data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400" data-testid="tab-elite">
              Elite
            </TabsTrigger>
          </TabsList>

          {/* EVALUATION TAB */}
          <TabsContent value="evaluation">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="bg-[hsl(220,20%,7%)] border border-white/10 mb-6">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-lg uppercase text-white">Two-Phase Evaluation</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="flex items-center gap-2 p-3 rounded bg-white/5 border border-white/5">
                      <Target className="w-4 h-4 text-blue-400 shrink-0" />
                      <div><div className="text-xs text-gray-500">Phase 1</div><div className="font-mono font-bold text-white">{tradingRules.profit_target_phase1}%</div></div>
                    </div>
                    <div className="flex items-center gap-2 p-3 rounded bg-white/5 border border-white/5">
                      <Shield className="w-4 h-4 text-blue-400 shrink-0" />
                      <div><div className="text-xs text-gray-500">Phase 2</div><div className="font-mono font-bold text-white">{tradingRules.profit_target_phase2}%</div></div>
                    </div>
                    <div className="flex items-center gap-2 p-3 rounded bg-white/5 border border-white/5">
                      <TrendingUp className="w-4 h-4 text-blue-400 shrink-0" />
                      <div><div className="text-xs text-gray-500">Daily Loss</div><div className="font-mono font-bold text-white">{tradingRules.max_daily_loss}%</div></div>
                    </div>
                    <div className="flex items-center gap-2 p-3 rounded bg-white/5 border border-white/5">
                      <TrendingUp className="w-4 h-4 text-blue-400 shrink-0" />
                      <div><div className="text-xs text-gray-500">Max DD</div><div className="font-mono font-bold text-white">{tradingRules.max_total_loss}%</div></div>
                    </div>
                    <div className="flex items-center gap-2 p-3 rounded bg-yellow-500/10 border border-yellow-500/20">
                      <Clock className="w-4 h-4 text-yellow-400 shrink-0" />
                      <div><div className="text-xs text-gray-500">Min. Days</div><div className="font-mono font-bold text-yellow-400">6</div></div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                {challengePackages.map((pkg, i) => (
                  <motion.div key={pkg.package_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.05 }}>
                    <Card className={`h-full bg-[hsl(220,20%,7%)] border transition-all duration-300 relative overflow-visible ${pkg.package_id === '50k' ? 'border-blue-500/50 mt-3' : 'border-white/10 hover:border-blue-500/30'}`} data-testid={`package-${pkg.package_id}`}>
                      {pkg.package_id === '50k' && <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-500 to-cyan-500 text-xs z-10">POPULAR</Badge>}
                      <CardContent className="p-3 sm:p-4 flex flex-col h-full">
                        <div className="text-center mb-3">
                          <h3 className="font-heading font-bold text-lg sm:text-xl md:text-2xl mb-1 text-white">{formatCurrency(pkg.account_size)}</h3>
                          <div className="font-mono text-lg sm:text-xl text-blue-400 font-bold">
                            {formatCurrency(getDisplayPrice(pkg))}
                            {(appliedCoupon && selectedPackage === pkg.package_id) || upgradeProfitSplit[pkg.package_id] || payoutOnDemand[pkg.package_id] ? (
                              <span className="line-through text-gray-500 text-sm ml-1">${pkg.price}</span>
                            ) : null}
                          </div>
                        </div>

                        <div className="space-y-1.5 flex-1 text-xs text-gray-300">
                          <div className="flex items-center gap-2"><Check className="w-3 h-3 text-blue-400 shrink-0" /><span>{pkg.profit_target_phase1 || tradingRules.profit_target_phase1}% Phase 1</span></div>
                          <div className="flex items-center gap-2"><Check className="w-3 h-3 text-blue-400 shrink-0" /><span>{pkg.profit_target_phase2 || tradingRules.profit_target_phase2}% Phase 2</span></div>
                          <div className="flex items-center gap-2"><Check className="w-3 h-3 text-blue-400 shrink-0" /><span className={upgradeProfitSplit[pkg.package_id] ? 'text-green-400 font-semibold' : ''}>{upgradeProfitSplit[pkg.package_id] ? '100% Split' : 'Up to 100% Split'}</span></div>
                        </div>

                        {/* Upgrade Options */}
                        <div className={`mt-2 p-2 rounded-lg border cursor-pointer transition-all ${upgradeProfitSplit[pkg.package_id] ? 'bg-green-500/10 border-green-500/50' : 'bg-white/5 border-white/10 hover:border-green-500/30'}`} onClick={() => toggleUpgrade(pkg.package_id)}>
                          <div className="flex items-center gap-2">
                            <Checkbox checked={upgradeProfitSplit[pkg.package_id] || false} onCheckedChange={() => toggleUpgrade(pkg.package_id)} className="data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500" />
                            <div className="flex-1">
                              <div className="flex items-center gap-1"><Sparkles className="w-3 h-3 text-green-400" /><span className="text-xs font-medium text-white">100% Split</span></div>
                              <p className="text-[10px] text-gray-500">+${getUpgradeFee(pkg.price).toFixed(0)}</p>
                            </div>
                          </div>
                        </div>

                        <div className={`mt-1.5 p-2 rounded-lg border cursor-pointer transition-all ${payoutOnDemand[pkg.package_id] ? 'bg-cyan-500/10 border-cyan-500/50' : 'bg-white/5 border-white/10 hover:border-cyan-500/30'}`} onClick={() => togglePayoutOnDemand(pkg.package_id)}>
                          <div className="flex items-center gap-2">
                            <Checkbox checked={payoutOnDemand[pkg.package_id] || false} onCheckedChange={() => togglePayoutOnDemand(pkg.package_id)} className="data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500" />
                            <div className="flex-1">
                              <div className="flex items-center gap-1"><Zap className="w-3 h-3 text-cyan-400" /><span className="text-xs font-medium text-white">On Demand</span></div>
                              <p className="text-[10px] text-gray-500">+${getPayoutOnDemandFee(pkg.price).toFixed(0)}</p>
                            </div>
                          </div>
                        </div>

                        {couponCode && !appliedCoupon && (
                          <Button variant="outline" size="sm" className="w-full mt-2 text-xs border-white/10 text-white hover:bg-white/5" onClick={() => handleValidateCoupon(pkg.package_id)} disabled={validatingCoupon}>
                            {validatingCoupon ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply Coupon'}
                          </Button>
                        )}

                        <Button className="w-full mt-2 font-heading uppercase tracking-wider text-xs bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600" onClick={() => handlePurchase(pkg.package_id)} disabled={purchasing === pkg.package_id} data-testid={`buy-${pkg.package_id}`}>
                          {purchasing === pkg.package_id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'GET STARTED'}
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </TabsContent>

          {/* INSTANT FUNDING TAB */}
          <TabsContent value="instant">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="bg-[hsl(220,20%,7%)] border border-cyan-500/30 mb-6">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-lg uppercase flex items-center gap-2 text-white">
                    <Zap className="w-5 h-5 text-cyan-400" />
                    Skip The Evaluation + Earn KBTCOIN
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="flex items-center gap-2 p-3 rounded bg-white/5 border border-white/5">
                      <Zap className="w-4 h-4 text-cyan-400 shrink-0" />
                      <div><div className="text-xs text-gray-500">Evaluation</div><div className="font-mono font-bold text-cyan-400">None</div></div>
                    </div>
                    <div className="flex items-center gap-2 p-3 rounded bg-white/5 border border-white/5">
                      <TrendingUp className="w-4 h-4 text-cyan-400 shrink-0" />
                      <div><div className="text-xs text-gray-500">Daily Loss</div><div className="font-mono font-bold text-white">{tradingRules.max_daily_loss}%</div></div>
                    </div>
                    <div className="flex items-center gap-2 p-3 rounded bg-white/5 border border-white/5">
                      <TrendingUp className="w-4 h-4 text-cyan-400 shrink-0" />
                      <div><div className="text-xs text-gray-500">Max DD</div><div className="font-mono font-bold text-white">{tradingRules.max_total_loss}%</div></div>
                    </div>
                    <div className="flex items-center gap-2 p-3 rounded bg-yellow-500/10 border border-yellow-500/20">
                      <Clock className="w-4 h-4 text-yellow-400 shrink-0" />
                      <div><div className="text-xs text-gray-500">Min. Days</div><div className="font-mono font-bold text-yellow-400">6</div></div>
                    </div>
                    <div className="flex items-center gap-2 p-3 rounded bg-yellow-500/10 border border-yellow-500/20">
                      <Coins className="w-4 h-4 text-yellow-400 shrink-0" />
                      <div><div className="text-xs text-gray-500">Bonus</div><div className="font-mono font-bold text-yellow-400">KBTCOIN</div></div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                {instantPackages.map((pkg, i) => {
                  const kbtReward = kbtcoinRewards[pkg.package_id] || 0;
                  return (
                    <motion.div key={pkg.package_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.05 }}>
                      <Card className="h-full bg-[hsl(220,20%,7%)] border border-cyan-500/30 hover:border-cyan-500/50 transition-all duration-300" data-testid={`package-${pkg.package_id}`}>
                        <CardContent className="p-3 sm:p-4 flex flex-col h-full">
                          <Badge className="mb-2 bg-cyan-500/20 text-cyan-400 border-cyan-500/30 w-fit text-xs">
                            <Zap className="w-3 h-3 mr-1" />INSTANT
                          </Badge>
                          <div className="text-center mb-3">
                            <h3 className="font-heading font-bold text-lg sm:text-xl md:text-2xl mb-1 text-white">{formatCurrency(pkg.account_size)}</h3>
                            <div className="font-mono text-lg sm:text-xl text-cyan-400 font-bold">
                              {formatCurrency(getDisplayPrice(pkg))}
                              {(appliedCoupon && selectedPackage === pkg.package_id) || upgradeProfitSplit[pkg.package_id] || payoutOnDemand[pkg.package_id] ? (
                                <span className="line-through text-gray-500 text-sm ml-1">${pkg.price}</span>
                              ) : null}
                            </div>
                          </div>

                          {kbtReward > 0 && (
                            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded p-2 mb-2 text-center">
                              <p className="text-yellow-400 font-bold text-sm flex items-center justify-center gap-1">
                                <Coins className="w-4 h-4" />+{kbtReward} KBTCOIN
                              </p>
                            </div>
                          )}

                          <div className="space-y-1.5 flex-1 text-xs text-gray-300">
                            <div className="flex items-center gap-2"><Check className="w-3 h-3 text-cyan-400 shrink-0" /><span>No Evaluation</span></div>
                            <div className="flex items-center gap-2"><Check className="w-3 h-3 text-cyan-400 shrink-0" /><span>Instant Access</span></div>
                            <div className="flex items-center gap-2"><Check className="w-3 h-3 text-cyan-400 shrink-0" /><span className={upgradeProfitSplit[pkg.package_id] ? 'text-green-400 font-semibold' : ''}>{upgradeProfitSplit[pkg.package_id] ? '100% Split' : '90% Split'}</span></div>
                          </div>

                          {/* Upgrade Options */}
                          <div className={`mt-2 p-2 rounded-lg border cursor-pointer transition-all ${upgradeProfitSplit[pkg.package_id] ? 'bg-green-500/10 border-green-500/50' : 'bg-white/5 border-white/10 hover:border-green-500/30'}`} onClick={() => toggleUpgrade(pkg.package_id)}>
                            <div className="flex items-center gap-2">
                              <Checkbox checked={upgradeProfitSplit[pkg.package_id] || false} onCheckedChange={() => toggleUpgrade(pkg.package_id)} className="data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500" />
                              <div className="flex-1">
                                <div className="flex items-center gap-1"><Sparkles className="w-3 h-3 text-green-400" /><span className="text-xs font-medium text-white">Upgrade to 100% Split</span></div>
                                <p className="text-[10px] text-gray-500">+${getUpgradeFee(pkg.price).toFixed(0)} (+25%)</p>
                              </div>
                            </div>
                          </div>

                          <div className={`mt-1.5 p-2 rounded-lg border cursor-pointer transition-all ${payoutOnDemand[pkg.package_id] ? 'bg-cyan-500/10 border-cyan-500/50' : 'bg-white/5 border-white/10 hover:border-cyan-500/30'}`} onClick={() => togglePayoutOnDemand(pkg.package_id)}>
                            <div className="flex items-center gap-2">
                              <Checkbox checked={payoutOnDemand[pkg.package_id] || false} onCheckedChange={() => togglePayoutOnDemand(pkg.package_id)} className="data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500" />
                              <div className="flex-1">
                                <div className="flex items-center gap-1"><Zap className="w-3 h-3 text-cyan-400" /><span className="text-xs font-medium text-white">On Demand</span></div>
                                <p className="text-[10px] text-gray-500">+${getPayoutOnDemandFee(pkg.price).toFixed(0)}</p>
                              </div>
                            </div>
                          </div>

                          {couponCode && !appliedCoupon && (
                            <Button variant="outline" size="sm" className="w-full mt-2 text-xs border-white/10 text-white hover:bg-white/5" onClick={() => handleValidateCoupon(pkg.package_id)} disabled={validatingCoupon}>
                              {validatingCoupon ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply Coupon'}
                            </Button>
                          )}

                          <Button className="w-full mt-2 font-heading uppercase tracking-wider text-xs bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white" onClick={() => handlePurchase(pkg.package_id)} disabled={purchasing === pkg.package_id} data-testid={`buy-${pkg.package_id}`}>
                            {purchasing === pkg.package_id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'GET FUNDED NOW'}
                          </Button>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </TabsContent>

          {/* ELITE FUNDED TAB */}
          <TabsContent value="elite">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              {/* Selection Notice */}
              <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-green-400 font-medium">Selection-Based Access Only</p>
                    <p className="text-gray-400 text-sm mt-1">
                      Elite Funded Trader accounts are not available for open purchase. 
                      You must first prove your skills on a Funded Instant account before applying.
                    </p>
                  </div>
                </div>
              </div>

              <Card className="bg-[hsl(220,20%,7%)] border border-green-500/30 mb-6">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-lg uppercase flex items-center gap-2 text-white">
                    <Trophy className="w-5 h-5 text-green-400" />
                    Elite Funded Trader - Real Capital
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="flex items-center gap-2 p-3 rounded bg-white/5 border border-white/5">
                      <Trophy className="w-4 h-4 text-green-400 shrink-0" />
                      <div><div className="text-xs text-gray-500">Capital</div><div className="font-mono font-bold text-green-400">Real</div></div>
                    </div>
                    <div className="flex items-center gap-2 p-3 rounded bg-white/5 border border-white/5">
                      <TrendingUp className="w-4 h-4 text-green-400 shrink-0" />
                      <div><div className="text-xs text-gray-500">Daily DD</div><div className="font-mono font-bold text-white">10%</div></div>
                    </div>
                    <div className="flex items-center gap-2 p-3 rounded bg-white/5 border border-white/5">
                      <TrendingUp className="w-4 h-4 text-green-400 shrink-0" />
                      <div><div className="text-xs text-gray-500">Max DD</div><div className="font-mono font-bold text-white">20%</div></div>
                    </div>
                    <div className="flex items-center gap-2 p-3 rounded bg-white/5 border border-white/5">
                      <Shield className="w-4 h-4 text-green-400 shrink-0" />
                      <div><div className="text-xs text-gray-500">Payouts</div><div className="font-mono font-bold text-green-400">100%</div></div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {eliteAccounts.map((account, index) => (
                  <Card key={account.id} className={`bg-[hsl(220,20%,7%)] border transition-all ${index === 2 ? 'border-green-500/50 relative' : 'border-green-500/30 hover:border-green-500/50'}`} data-testid={`elite-${account.id}`}>
                    {index === 2 && <div className="bg-green-500 text-white text-xs font-bold px-3 py-1 text-center rounded-t-lg">MOST POPULAR</div>}
                    <CardContent className="p-4 sm:p-6 text-center">
                      <Trophy className="w-8 h-8 text-green-400 mx-auto mb-3" />
                      <h4 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-1">{formatCurrency(account.size)}</h4>
                      <p className="text-2xl sm:text-3xl font-bold text-green-400 mb-4">{formatCurrency(account.price)}</p>
                      <div className="space-y-2 text-sm text-left mb-4">
                        <div className="flex justify-between py-2 border-b border-white/5"><span className="text-gray-400">Daily Drawdown</span><span className="text-white font-medium">{account.dailyDrawdown}%</span></div>
                        <div className="flex justify-between py-2 border-b border-white/5"><span className="text-gray-400">Max Drawdown</span><span className="text-white font-medium">{account.maxDrawdown}%</span></div>
                        <div className="flex justify-between py-2 border-b border-white/5"><span className="text-gray-400">Profit Split</span><span className="text-green-400 font-bold">{account.profitSplit}%</span></div>
                        <div className="flex justify-between py-2"><span className="text-gray-400">Payouts</span><span className="text-green-400 font-bold">100% Guaranteed</span></div>
                      </div>
                      <Button className="w-full bg-green-500 hover:bg-green-600" onClick={() => navigate('/elite-funded')}>Learn More</Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </TabsContent>
        </Tabs>
        </div>

        {/* Additional Info */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mt-8 md:mt-12 text-center text-xs md:text-sm text-gray-500">
          <p>All prices are in USD. Payments are processed securely via Stripe.</p>
          <p className="mt-2">Have questions? Check our <a href="/faq" className="text-blue-400 hover:underline">FAQ</a> or contact <a href="mailto:support@traderscapital.net" className="text-blue-400 hover:underline">support@traderscapital.net</a></p>
        </motion.div>
      </div>
    </div>
  );
};

export default GetFunded;
